import { useEffect, useCallback, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { X, ChevronLeft, ChevronRight, Download, ImageIcon } from 'lucide-react';
import { Button } from './button';

interface ImageCarouselModalProps {
  images: Array<{ id: string; imageUrl: string; }>;
  initialIndex: number;
  onClose: () => void;
}

export function ImageCarouselModal({ images, initialIndex, onClose }: ImageCarouselModalProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    startIndex: initialIndex,
    loop: false,
    dragFree: false,
  });
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Track current slide
  useEffect(() => {
    if (!emblaApi) return;
    
    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };
    
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // Close on ESC key and prevent body scroll
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') scrollPrev();
      if (e.key === 'ArrowRight') scrollNext();
    };
    
    // Prevent body scroll when modal is open
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalStyle;
    };
  }, [onClose, scrollPrev, scrollNext]);

  const handleDownload = async (imageUrl: string, index: number) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `productshot-${index + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback to direct link
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `productshot-${index + 1}.jpg`;
      link.click();
    }
  };

  // Prevent touch move on background to stop page scrolling
  const handleTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col touch-none"
      onTouchMove={handleTouchMove}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 md:p-4 shrink-0">
        <div className="text-white text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 md:h-6 md:w-6 text-white" />
        </button>
      </div>

      {/* Carousel */}
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-2 md:px-4">
        {/* Previous Button - Hidden on mobile, use swipe instead */}
        {images.length > 1 && (
          <button
            onClick={scrollPrev}
            className="hidden md:flex absolute left-2 md:left-4 z-10 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </button>
        )}

        {/* Embla Carousel */}
        <div className="w-full max-w-5xl overflow-hidden" ref={emblaRef}>
          <div className="flex touch-pan-y">
            {images.map((img, index) => (
              <div 
                key={img.id} 
                className="flex-[0_0_100%] min-w-0 flex items-center justify-center px-2"
              >
                <CarouselImage
                  imageUrl={img.imageUrl}
                  alt={`Image ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Next Button - Hidden on mobile, use swipe instead */}
        {images.length > 1 && (
          <button
            onClick={scrollNext}
            className="hidden md:flex absolute right-2 md:right-4 z-10 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </button>
        )}
      </div>

      {/* Footer with Download */}
      <div className="p-3 md:p-4 flex flex-col items-center gap-3 shrink-0">
        {/* Dots Indicator */}
        {images.length > 1 && (
          <div className="flex gap-1.5">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        )}
        
        {/* Download Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDownload(images[currentIndex].imageUrl, currentIndex)}
          className="bg-white/10 hover:bg-white/20 text-white border-white/20"
        >
          <Download className="h-4 w-4 mr-2" />
          Baixar
        </Button>
      </div>
    </div>
  );
}

// Sub-component to handle image loading with extension fallback
function CarouselImage({ imageUrl, alt }: { imageUrl: string; alt: string }) {
  const [currentSrc, setCurrentSrc] = useState(imageUrl);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    // Try different extensions if the original fails
    const extensions = ['png', 'jpeg', 'jpg'];
    const urlWithoutQuery = currentSrc.split('?')[0];
    const currentExt = urlWithoutQuery.split('.').pop();
    const basePath = urlWithoutQuery.substring(0, urlWithoutQuery.lastIndexOf('.'));
    const queryString = currentSrc.includes('?') ? currentSrc.split('?')[1] : '';
    
    const currentExtIndex = extensions.indexOf(currentExt || '');
    const nextExtIndex = currentExtIndex + 1;
    
    if (nextExtIndex < extensions.length) {
      const newSrc = `${basePath}.${extensions[nextExtIndex]}${queryString ? '?' + queryString : ''}`;
      setCurrentSrc(newSrc);
      setHasError(false);
    } else {
      setHasError(true);
    }
  };

  if (hasError) {
    return (
      <div className="max-h-[60vh] md:max-h-[70vh] flex items-center justify-center text-white/50">
        <ImageIcon className="h-16 w-16" />
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className="max-h-[60vh] md:max-h-[70vh] max-w-full object-contain rounded-lg select-none"
      draggable={false}
      onError={handleError}
    />
  );
}

